/**
 * Lane 1398 Phase B (Ryan, 2026-10-07): after a workbook is built in the
 * chat, typing locks until the buyer picks "Change this workbook" or "Ask
 * about something else". The click is the signal; nothing guesses from the
 * buyer's words.
 *
 * One conversation-scoped state machine (Codex r1):
 *   normal     -> a job row INSIDE a message reports ready for a job not
 *                 yet acknowledged, and newer than any seen  -> choice
 *   choice     -> chooseOther -> normal (job acknowledged)
 *              -> chooseChange -> editing
 *   editing    -> cancelEdit -> choice; beginSubmit -> submitting
 *   submitting -> finishSubmit: a re-armed change -> editing (text kept),
 *                 anything else -> normal
 *              -> failSubmit -> editing (text and target kept)
 *
 * Rehydrated job rows never call reportReady, so they never lock. A
 * remounted or re-polled row cannot lock twice (acknowledged ids), and an
 * older row cannot take the lock from a newer one.
 */
import { useCallback, useEffect, useReducer, useRef } from 'react';

const INITIAL = { mode: 'normal', target: null, draft: '' };

function reducer(state, action) {
  switch (action.type) {
    case 'ready':
      return { mode: 'choice', target: action.target, draft: '' };
    case 'other':
      return INITIAL;
    case 'change':
      return state.mode === 'choice' ? { ...state, mode: 'editing' } : state;
    case 'cancel':
      return state.mode === 'editing' ? { ...state, mode: 'choice' } : state;
    case 'submit':
      return { ...state, mode: 'submitting', draft: action.draft };
    case 'rearm':
      return { mode: 'editing', draft: state.draft,
               target: { ...state.target, token: action.token } };
    case 'retry':
      return { mode: 'editing', draft: action.draft || '',
               target: action.target };
    case 'failed':
      return { ...state, mode: 'editing' };
    case 'reset':
      return INITIAL;
    default:
      return state;
  }
}

export function useWorkbookChoice(conversationId) {
  const [state, rawDispatch] = useReducer(reducer, INITIAL);
  const acknowledged = useRef(new Set());
  const newestJobId = useRef(0);
  // The text of the change that started each change job, so a change whose
  // BUILD fails can be retried with the buyer's words still in place.
  const changeDrafts = useRef(new Map());
  // The send path reads the state between awaits and in `finally`; it must
  // see each transition at once, not on the next render. Every transition
  // goes through `dispatch`, which updates this ref synchronously.
  const stateRef = useRef(INITIAL);
  const dispatch = useCallback((action) => {
    stateRef.current = reducer(stateRef.current, action);
    rawDispatch(action);
  }, []);

  useEffect(() => {
    acknowledged.current = new Set();
    newestJobId.current = 0;
    dispatch({ type: 'reset' });
  }, [conversationId, dispatch]);

  const reportReady = useCallback((job) => {
    if (!job || job.status !== 'ready' || !job.change_token
        || !job.report_id) return;
    const jobId = Number(job.job_id);
    if (acknowledged.current.has(jobId) || jobId < newestJobId.current) return;
    newestJobId.current = jobId;
    acknowledged.current.add(jobId);
    dispatch({ type: 'ready', target: { jobId, reportId: job.report_id,
                                         token: job.change_token } });
  }, [dispatch]);

  const chooseOther = useCallback(() => dispatch({ type: 'other' }), [dispatch]);
  const chooseChange = useCallback(() => dispatch({ type: 'change' }), [dispatch]);
  const cancelEdit = useCallback(() => dispatch({ type: 'cancel' }), [dispatch]);
  const reset = useCallback(() => dispatch({ type: 'reset' }), [dispatch]);

  /** Called by the send path. Returns the `workbook_change` body field
   *  when this message is a change, else null. */
  const beginSubmit = useCallback((text) => {
    const current = stateRef.current;
    if (current.mode !== 'editing' || !current.target) return null;
    dispatch({ type: 'submit', draft: text });
    return { token: current.target.token };
  }, [dispatch]);

  /** Called with the response data of a change turn. Returns the text to
   *  put back in the composer when the change must be tried again. */
  const finishSubmit = useCallback((data) => {
    const current = stateRef.current;
    if (current.mode !== 'submitting') return null;
    const retry = data && data.report_change_retry;
    if (retry && retry.token) {
      dispatch({ type: 'rearm', token: retry.token });
      return current.draft;
    }
    const jobId = data && data.report_job && data.report_job.job_id;
    if (jobId) changeDrafts.current.set(Number(jobId), current.draft);
    dispatch({ type: 'other' });
    return null;
  }, [dispatch]);

  /** A change whose build failed: back to editing the SAME workbook with
   *  the buyer's words. Returns the text for the composer. */
  const retryChange = useCallback((job) => {
    if (!job || !job.change_token) return null;
    const draft = changeDrafts.current.get(Number(job.job_id)) || '';
    dispatch({ type: 'retry', draft,
               target: { jobId: Number(job.job_id),
                         reportId: job.report_id || null,
                         token: job.change_token } });
    return draft;
  }, [dispatch]);

  /** Called when a change turn failed in transport; returns the text to put
   *  back in the composer. */
  const failSubmit = useCallback(() => {
    const current = stateRef.current;
    if (current.mode !== 'submitting') return null;
    dispatch({ type: 'failed' });
    return current.draft;
  }, [dispatch]);

  return {
    mode: state.mode,
    draft: state.draft,
    locked: state.mode === 'choice' || state.mode === 'submitting',
    editing: state.mode === 'editing',
    reportReady, chooseOther, chooseChange, cancelEdit, reset,
    beginSubmit, finishSubmit, failSubmit, retryChange,
  };
}
